import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNotification } from '../context/NotificationContext';
import i18n from '../i18n';
import { API_URL } from '../config/api';
import { registerPushSubscription } from '../utils/pushNotifications';
import './Settings.css';

const Settings = () => {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const { preference, setPreference } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    birthdate: '',
    phoneNumber: ''
  });
  const [settings, setSettings] = useState({
    pushNotifications: true,
    emailNotifications: true,
    smsNotifications: true,
    language: 'en',
    themePreference: 'device'
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: '',
    street: '',
    city: '',
    zipCode: '',
    addressDetails: '',
    deliveryInstructions: '',
    coordinates: { lat: 0, lng: 0 }
  });
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [testPushLoading, setTestPushLoading] = useState(false);
  const addressInputRef = useRef(null);
  const mapPickerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [googleMapsReady, setGoogleMapsReady] = useState(!!(typeof window !== 'undefined' && window.google && window.google.maps));

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        birthdate: user.birthdate ? user.birthdate.split('T')[0] : '',
        phoneNumber: user.phoneNumber || ''
      });
      if (user.settings) {
        setSettings({
          pushNotifications: user.settings.pushNotifications ?? true,
          emailNotifications: user.settings.emailNotifications ?? true,
          smsNotifications: user.settings.smsNotifications ?? true,
          language: user.settings.language || 'en',
          themePreference: user.settings.themePreference || 'device'
        });
        if (user.settings.themePreference) {
          setPreference(user.settings.themePreference);
        }
      }
    }
  }, [user]);

  // Fetch fresh user data on component mount to ensure savedAddresses are loaded
  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        try {
          const response = await axios.get(`${API_URL}/users/me`);
          if (response.data) {
            updateUser(response.data);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // When user has push enabled, ensure subscription is sent to backend (e.g. after new device or SW update)
  useEffect(() => {
    if (!user?.settings?.pushNotifications) return;
    registerPushSubscription()
      .then((result) => {
        if (!result.ok && result.reason === 'server') {
          console.warn('Push: server not configured. Set VAPID_PUBLIC_KEY and VAPID_PRIVATE_KEY in production .env');
        }
      })
      .catch(() => {});
  }, [user?.settings?.pushNotifications]);

  // Ensure we know when Google Maps is ready (for map picker)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.google && window.google.maps) {
      setGoogleMapsReady(true);
      return;
    }
    const id = setInterval(() => {
      if (typeof window !== 'undefined' && window.google && window.google.maps) {
        setGoogleMapsReady(true);
        clearInterval(id);
      }
    }, 150);
    return () => clearInterval(id);
  }, []);

  // Initialize Google Places Autocomplete for address verification
  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places && addressInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        { types: ['address'] }
      );

      const extractCity = (components) => {
        for (let component of components) {
          if (component.types.includes('locality')) {
            return component.long_name;
          }
          if (component.types.includes('administrative_area_level_1')) {
            return component.long_name;
          }
        }
        return '';
      };

      const extractZipCode = (components) => {
        for (let component of components) {
          if (component.types.includes('postal_code')) {
            return component.long_name;
          }
        }
        return '';
      };

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry && place.geometry.location) {
          setNewAddress((prev) => ({
            ...prev,
            street: place.formatted_address,
            city: extractCity(place.address_components),
            zipCode: extractZipCode(place.address_components),
            coordinates: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            }
          }));
        }
      });
    }
  }, []);

  // Initialize map picker (draggable marker for exact delivery location)
  useEffect(() => {
    if (!googleMapsReady || !mapPickerRef.current || !window.google?.maps) return;
    const lat = newAddress.coordinates?.lat;
    const lng = newAddress.coordinates?.lng;
    const hasCoords = typeof lat === 'number' && typeof lng === 'number' && (lat !== 0 || lng !== 0);
    const center = hasCoords ? { lat, lng } : { lat: 60.1699, lng: 24.9384 };

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapPickerRef.current, {
        zoom: hasCoords ? 17 : 12,
        center,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true
      });
      markerRef.current = new window.google.maps.Marker({
        position: center,
        map: mapInstanceRef.current,
        draggable: true,
        title: 'Drag to set exact delivery location'
      });
      markerRef.current.addListener('dragend', () => {
        const pos = markerRef.current.getPosition();
        if (pos) {
          setNewAddress((prev) => ({
            ...prev,
            coordinates: { lat: pos.lat(), lng: pos.lng() }
          }));
        }
      });
      // Allow click-to-place as well
      mapInstanceRef.current.addListener('click', (e) => {
        const latLng = e.latLng;
        if (latLng && markerRef.current) {
          markerRef.current.setPosition(latLng);
          setNewAddress((prev) => ({
            ...prev,
            coordinates: { lat: latLng.lat(), lng: latLng.lng() }
          }));
        }
      });
    }
  }, [googleMapsReady]);

  // Sync marker and map view when coordinates change (e.g. from address search)
  useEffect(() => {
    if (!googleMapsReady || !mapInstanceRef.current || !markerRef.current) return;
    const lat = newAddress.coordinates?.lat;
    const lng = newAddress.coordinates?.lng;
    const hasCoords = typeof lat === 'number' && typeof lng === 'number' && (lat !== 0 || lng !== 0);
    if (!hasCoords) return;
    const pos = { lat, lng };
    markerRef.current.setPosition(pos);
    mapInstanceRef.current.panTo(pos);
    mapInstanceRef.current.setZoom(17);
  }, [googleMapsReady, newAddress.coordinates?.lat, newAddress.coordinates?.lng]);

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleLanguageChange = async (lang) => {
    const newSettings = { ...settings, language: lang };
    setSettings(newSettings);
    await updateSettings(newSettings);
    i18n.changeLanguage(lang);
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await axios.put(`${API_URL}/users/profile`, profileData);
      updateUser({ ...user, ...response.data });
      setMessage(t('settings.updated'));
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const response = await axios.put(`${API_URL}/users/settings`, newSettings);
      updateUser({ ...user, settings: response.data.settings });
      if (newSettings.language) {
        i18n.changeLanguage(newSettings.language);
      }
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleThemeChange = async (value) => {
    setPreference(value);
    setSettings(prev => ({ ...prev, themePreference: value }));
    if (user) {
      await updateSettings({ ...settings, themePreference: value });
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    await updateSettings(settings);
    if (settings.pushNotifications) {
      const result = await registerPushSubscription();
      if (!result.ok) {
        const msg = result.reason === 'server'
          ? (t('settings.pushNotConfigured') || 'Settings saved. Push notifications are not configured on the server (contact support for production).')
          : result.reason === 'permission'
            ? (t('settings.pushPermissionDenied') || 'Settings saved. Please allow notifications in your browser or device.')
            : (t('settings.pushSubscribeFailed') || 'Settings saved. Could not activate push notifications.');
        showError(msg);
      }
    }
    setMessage(t('settings.updated'));
    setTimeout(() => setMessage(''), 3000);
    setLoading(false);
  };

  const handleSendTestPush = async () => {
    setTestPushLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/users/me/test-push`);
      showSuccess(data.message || t('settings.testPushSent'));
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.status === 503
        ? (t('settings.pushNotConfigured') || 'Push not configured on server.')
        : err.response?.status === 400
          ? (err.response?.data?.message?.includes('subscription') ? t('settings.testPushNoSubscription') : err.response?.data?.message)
          : t('settings.testPushFailed');
      showError(msg);
    } finally {
      setTestPushLoading(false);
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    const hasCoords = newAddress.coordinates?.lat != null && newAddress.coordinates?.lng != null &&
      (Number(newAddress.coordinates.lat) !== 0 || Number(newAddress.coordinates.lng) !== 0);
    if (!hasCoords) {
      setMessage(t('settings.setLocationOnMap') || 'Please search for an address above or set your exact delivery location on the map.');
      return;
    }
    setLoading(true);
    setMessage('');

    try {
      const response = await axios.post(`${API_URL}/users/addresses`, newAddress);
      updateUser({ ...user, savedAddresses: response.data.savedAddresses });
      setNewAddress({ label: '', street: '', city: '', zipCode: '', addressDetails: '', deliveryInstructions: '', coordinates: { lat: 0, lng: 0 } });
      setMessage(t('settings.addressAdded') || 'Address added successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to add address');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAddress = (address) => {
    setEditingAddressId(address._id);
    setNewAddress({
      label: address.label || '',
      street: address.street || '',
      city: address.city || '',
      zipCode: address.zipCode || '',
      addressDetails: address.addressDetails || '',
      deliveryInstructions: address.deliveryInstructions || '',
      coordinates: address.coordinates
        ? { lat: address.coordinates.lat, lng: address.coordinates.lng }
        : { lat: 0, lng: 0 }
    });
  };

  const handleCancelEdit = () => {
    setEditingAddressId(null);
    setNewAddress({ label: '', street: '', city: '', zipCode: '', addressDetails: '', deliveryInstructions: '', coordinates: { lat: 0, lng: 0 } });
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();
    if (!editingAddressId) return;
    const hasCoords = newAddress.coordinates?.lat != null && newAddress.coordinates?.lng != null &&
      (Number(newAddress.coordinates.lat) !== 0 || Number(newAddress.coordinates.lng) !== 0);
    if (!hasCoords) {
      setMessage(t('settings.setLocationOnMap') || 'Please search for an address above or set your exact delivery location on the map.');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const response = await axios.put(`${API_URL}/users/addresses/${editingAddressId}`, newAddress);
      updateUser({ ...user, savedAddresses: response.data.savedAddresses });
      handleCancelEdit();
      setMessage(t('settings.addressUpdated') || 'Address updated successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to update address');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm(t('settings.confirmDelete') || 'Are you sure you want to delete this address?')) {
      return;
    }

    try {
      const response = await axios.delete(`${API_URL}/users/addresses/${addressId}`);
      updateUser({ ...user, savedAddresses: response.data.savedAddresses });
      if (editingAddressId === addressId) handleCancelEdit();
      setMessage(t('settings.addressDeleted') || 'Address deleted successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to delete address');
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      const msg = t('settings.passwordMismatch') || 'New passwords do not match';
      setMessage(msg);
      showError(msg);
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      const msg = t('settings.passwordTooShort') || 'Password must be at least 6 characters';
      setMessage(msg);
      showError(msg);
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.put(`${API_URL}/users/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      const successMsg = t('settings.passwordChanged') || 'Password changed successfully';
      showSuccess(successMsg);
      setMessage('');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      const errMsg = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Failed to change password';
      setMessage(errMsg);
      showError(errMsg);
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="container">
        <h1>{t('settings.title')}</h1>

        <div className="settings-sections">
          <div className="settings-section">
            <h2>{t('settings.profile')}</h2>
            <form onSubmit={updateProfile}>
              <div className="form-group">
                <label>{t('auth.firstName')}</label>
                <input
                  type="text"
                  name="firstName"
                  value={profileData.firstName}
                  onChange={handleProfileChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('auth.lastName')}</label>
                <input
                  type="text"
                  name="lastName"
                  value={profileData.lastName}
                  onChange={handleProfileChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('auth.email')}</label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('auth.birthdate')}</label>
                <input
                  type="date"
                  name="birthdate"
                  value={profileData.birthdate}
                  onChange={handleProfileChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('auth.phoneNumber')}</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={profileData.phoneNumber}
                  onChange={handleProfileChange}
                  required
                />
              </div>
              {message && <div className="success-message">{message}</div>}
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {t('settings.update')}
              </button>
            </form>
          </div>

          <div className="settings-section">
            <h2>{t('settings.changePassword') || 'Change Password'}</h2>
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label>{t('settings.currentPassword') || 'Current Password'}</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('settings.newPassword') || 'New Password'}</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength={6}
                />
              </div>
              <div className="form-group">
                <label>{t('settings.confirmPassword') || 'Confirm New Password'}</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength={6}
                />
              </div>
              {message && <div className={`${message.includes('successfully') ? 'success-message' : 'error-message'}`}>{message}</div>}
              <button type="submit" className="btn btn-primary" disabled={passwordLoading}>
                {passwordLoading ? (t('settings.updating') || 'Updating...') : (t('settings.changePassword') || 'Change Password')}
              </button>
            </form>
          </div>

          <div className="settings-section">
            <h2>{t('settings.appearance') || 'Appearance'}</h2>
            <p className="settings-notifications-intro">
              {t('settings.appearanceIntro') || 'Choose light, dark, or follow your device setting.'}
            </p>
            <div className="form-group settings-theme-row">
              <label>{t('settings.theme') || 'Theme'}</label>
              <select
                value={preference}
                onChange={(e) => handleThemeChange(e.target.value)}
                aria-label={t('settings.theme') || 'Theme'}
              >
                <option value="device">{t('settings.themeFollowDevice') || 'Follow device'}</option>
                <option value="light">{t('settings.themeLight') || 'Light'}</option>
                <option value="dark">{t('settings.themeDark') || 'Dark'}</option>
              </select>
            </div>
          </div>

          <div className="settings-section">
            <h2>{t('settings.notifications')}</h2>
            <p className="settings-notifications-intro">
              {t('settings.notificationsIntro') || 'Choose how you want to receive order updates and promotions.'}
            </p>
            <form onSubmit={handleSettingsSubmit}>
              <div className="notification-cards">
                <div className="notification-card">
                  <div className="notification-card-icon" aria-hidden="true">
                    <i className="fas fa-bell"></i>
                  </div>
                  <div className="notification-card-content">
                    <span className="notification-card-title">{t('settings.pushNotifications')}</span>
                    <span className="notification-card-desc">
                      {t('settings.pushNotificationsDesc') || 'Receive order status updates in your browser.'}
                    </span>
                    {settings.pushNotifications && (
                      <button
                        type="button"
                        className="settings-test-push-btn"
                        onClick={handleSendTestPush}
                        disabled={testPushLoading}
                      >
                        {testPushLoading ? (t('auth.sending') || 'Sending...') : t('settings.sendTestPush')}
                      </button>
                    )}
                  </div>
                  <label className="notification-toggle">
                    <input
                      type="checkbox"
                      name="pushNotifications"
                      checked={settings.pushNotifications}
                      onChange={handleSettingsChange}
                    />
                    <span className="notification-slider"></span>
                  </label>
                </div>
                <div className="notification-card">
                  <div className="notification-card-icon" aria-hidden="true">
                    <i className="fas fa-envelope"></i>
                  </div>
                  <div className="notification-card-content">
                    <span className="notification-card-title">{t('settings.emailNotifications')}</span>
                    <span className="notification-card-desc">
                      {t('settings.emailNotificationsDesc') || 'Get order confirmations and updates by email.'}
                    </span>
                  </div>
                  <label className="notification-toggle">
                    <input
                      type="checkbox"
                      name="emailNotifications"
                      checked={settings.emailNotifications}
                      onChange={handleSettingsChange}
                    />
                    <span className="notification-slider"></span>
                  </label>
                </div>
                <div className="notification-card">
                  <div className="notification-card-icon" aria-hidden="true">
                    <i className="fas fa-sms"></i>
                  </div>
                  <div className="notification-card-content">
                    <span className="notification-card-title">{t('settings.smsNotifications')}</span>
                    <span className="notification-card-desc">
                      {t('settings.smsNotificationsDesc') || 'Order updates via text message.'}
                    </span>
                  </div>
                  <label className="notification-toggle">
                    <input
                      type="checkbox"
                      name="smsNotifications"
                      checked={settings.smsNotifications}
                      onChange={handleSettingsChange}
                    />
                    <span className="notification-slider"></span>
                  </label>
                </div>
              </div>
              <div className="form-group settings-language-row">
                <label>{t('settings.language')}</label>
                <select
                  name="language"
                  value={settings.language}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                >
                  <option value="en">English</option>
                  <option value="fi">Suomi (Finnish)</option>
                </select>
              </div>
              {message && <div className="success-message">{message}</div>}
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {t('settings.update')}
              </button>
            </form>
          </div>

          <div className="settings-section">
            <h2>{t('settings.savedAddresses') || 'Saved Addresses'}</h2>
            {user?.savedAddresses && user.savedAddresses.length > 0 ? (
              <div className="addresses-list">
                {user.savedAddresses.map((address) => (
                  <div key={address._id} className="address-item">
                    <div>
                      <strong>{address.label}</strong>
                      <p>{address.street}, {address.city} {address.zipCode}{address.addressDetails ? ` — ${address.addressDetails}` : ''}</p>
                      {address.deliveryInstructions && (
                        <p className="address-delivery-instructions">{t('settings.deliveryInstructionsShort') || 'Driver instructions'}: {address.deliveryInstructions}</p>
                      )}
                    </div>
                    <div className="address-actions">
                      <button
                        type="button"
                        onClick={() => handleEditAddress(address)}
                        className="btn btn-secondary btn-sm"
                      >
                        {t('settings.editAddress') || 'Edit'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteAddress(address._id)}
                        className="btn btn-secondary btn-sm"
                      >
                        {t('settings.remove') || 'Remove'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>{t('settings.noAddresses') || 'No saved addresses'}</p>
            )}
            <form onSubmit={editingAddressId ? handleUpdateAddress : handleAddAddress} className="add-address-form">
              <h3>{editingAddressId ? (t('settings.editAddress') || 'Edit Address') : (t('settings.addAddress') || 'Add New Address')}</h3>
              <div className="form-group">
                <label>{t('settings.addressLabel') || 'Label (e.g., Home, Work)'}</label>
                <input
                  type="text"
                  name="label"
                  value={newAddress.label || ''}
                  onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('settings.street') || 'Street'}</label>
                <input
                  ref={addressInputRef}
                  type="text"
                  name="street"
                  value={newAddress.street || ''}
                  onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })}
                  placeholder={t('settings.searchAddress') || 'Search and select address...'}
                  required
                />
                <small className="form-help-text">
                  {t('settings.addressVerificationHelp') || 'Start typing to search and verify your address'}
                </small>
              </div>
              <div className="form-group">
                <label>{t('settings.city') || 'City'}</label>
                <input
                  type="text"
                  name="city"
                  value={newAddress.city || ''}
                  onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('settings.zipCode') || 'Zip Code'}</label>
                <input
                  type="text"
                  name="zipCode"
                  value={newAddress.zipCode || ''}
                  onChange={(e) => setNewAddress({ ...newAddress, zipCode: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{t('settings.addressDetails') || 'Staircase, apartment number (optional)'}</label>
                <input
                  type="text"
                  name="addressDetails"
                  value={newAddress.addressDetails || ''}
                  onChange={(e) => setNewAddress({ ...newAddress, addressDetails: e.target.value })}
                  placeholder={t('settings.addressDetailsPlaceholder') || 'e.g. B, 12'}
                />
              </div>
              <div className="form-group">
                <label>{t('settings.deliveryInstructions') || 'Instructions for delivery driver (optional)'}</label>
                <textarea
                  name="deliveryInstructions"
                  value={newAddress.deliveryInstructions || ''}
                  onChange={(e) => setNewAddress({ ...newAddress, deliveryInstructions: e.target.value })}
                  placeholder={t('settings.deliveryInstructionsPlaceholder') || 'e.g. Ring doorbell, Leave at door, Door code'}
                  rows={2}
                  maxLength={500}
                />
                <small className="form-help-text">
                  {t('settings.deliveryInstructionsHelp') || 'These will be pre-filled in the cart when you select this address.'}
                </small>
              </div>
              {googleMapsReady && (
                <div className="form-group address-map-picker">
                  <label>{t('settings.pinExactLocation') || 'Pin exact delivery location'}</label>
                  <p className="form-help-text map-picker-help">
                    {t('settings.pinExactLocationHelp') || 'Search for your address above, then drag the marker to your door or click on the map to set the exact spot drivers will see.'}
                  </p>
                  <div ref={mapPickerRef} className="address-map-picker-map" aria-hidden="true" />
                </div>
              )}
              <div className="address-form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingAddressId ? (t('settings.updateAddress') || 'Update Address') : (t('settings.add') || 'Add Address')}
                </button>
                {editingAddressId && (
                  <button type="button" onClick={handleCancelEdit} className="btn btn-secondary">
                    {t('settings.cancel') || 'Cancel'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
